import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock3, ShieldAlert, ArrowRight } from 'lucide-react';
import { checkPaymentStatus } from '../lib/paymentService';

type VerificationState = 'CHECKING' | 'PENDING' | 'SUCCESS' | 'FAILED' | 'UNAVAILABLE';

export const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateData = (location.state as Record<string, unknown> | null) || {};

  const rawOrderId = stateData.orderId || stateData.order_id;
  const orderId = typeof rawOrderId === 'string' ? rawOrderId.trim() : '';
  const rawTarget = stateData.targetNext;
  const targetNext =
    typeof rawTarget === 'string' &&
    rawTarget.startsWith('/') &&
    !rawTarget.startsWith('//') &&
    !rawTarget.includes('\\')
      ? rawTarget
      : '/dashboard';

  const [status, setStatus] = useState<VerificationState>(
    orderId ? 'CHECKING' : 'UNAVAILABLE'
  );
  const [record, setRecord] = useState<Awaited<ReturnType<typeof checkPaymentStatus>>>(null);

  useEffect(() => {
    if (!orderId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const verify = async () => {
      const result = await checkPaymentStatus(orderId);
      if (cancelled) return;

      if (result?.status === 'SUCCESS' && result.is_active === true &&
          result.webhook_verified === 'YES' && result.payment_id) {
        setRecord(result);
        setStatus('SUCCESS');
        return;
      }

      if (result?.status === 'FAILED') {
        setRecord(result);
        setStatus('FAILED');
        return;
      }

      setRecord(result);
      setStatus(result ? 'PENDING' : 'UNAVAILABLE');
      timer = setTimeout(verify, 3000);
    };

    void verify();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId]);

  useEffect(() => {
    if (status !== 'SUCCESS') return;
    const timer = setTimeout(() => navigate(targetNext, { replace: true }), 3000);
    return () => clearTimeout(timer);
  }, [status, navigate, targetNext]);

  const heading = {
    CHECKING: 'Verifying payment',
    PENDING: 'Payment confirmation pending',
    SUCCESS: 'Payment Successful',
    FAILED: 'Payment not completed',
    UNAVAILABLE: 'Unable to verify payment',
  }[status];

  const description = {
    CHECKING: 'Checking the order with the authenticated payment backend.',
    PENDING: 'We have not received final payment confirmation yet. Please wait; do not pay again until you check the payment status.',
    SUCCESS: 'The backend confirmed the payment, webhook verification, and active membership.',
    FAILED: 'The backend reports that this payment did not complete. Your membership has not been confirmed by this page.',
    UNAVAILABLE: 'A real order ID and an authenticated backend status response are required. No payment success is assumed.',
  }[status];

  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <section className="max-w-md w-full rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 text-center shadow-xl space-y-5">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
          {status === 'SUCCESS'
            ? <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            : status === 'FAILED' || status === 'UNAVAILABLE'
              ? <ShieldAlert className="h-10 w-10 text-rose-600" />
              : <Clock3 className="h-10 w-10 text-amber-600" />}
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-black text-slate-900">{heading}</h1>
          <p className="text-sm text-slate-600">{description}</p>
        </div>

        {orderId && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-left">
            <p className="text-xs text-slate-500">Order ID</p>
            <p className="break-all font-mono text-sm text-slate-800">{orderId}</p>
            {record?.payment_id && (
              <>
                <p className="mt-3 text-xs text-slate-500">Verified Payment ID</p>
                <p className="break-all font-mono text-sm text-slate-800">{record.payment_id}</p>
              </>
            )}
            {status === 'SUCCESS' && record && (
              <>
                <p className="mt-3 text-xs text-slate-500">Plan</p>
                <p className="text-sm font-semibold text-slate-800">{record.plan_name || 'Membership'}</p>
                <p className="mt-3 text-xs text-slate-500">Verified amount</p>
                <p className="text-sm font-semibold text-slate-800">
                  ₹{record.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="mt-3 text-xs text-slate-500">Membership ID</p>
                <p className="break-all font-mono text-sm text-slate-800">
                  {record.membership_id || 'Not supplied by backend'}
                </p>
              </>
            )}
          </div>
        )}

        {status === 'SUCCESS' && (
          <p className="text-xs text-emerald-700">
            Verified by backend. Redirecting in 3 seconds…
          </p>
        )}

        {status !== 'SUCCESS' && (
          <button
            type="button"
            onClick={() => navigate('/dashboard', { replace: true })}
            className="w-full rounded-xl border border-slate-300 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            Return to dashboard
            <ArrowRight className="ml-2 inline h-4 w-4" />
          </button>
        )}
      </section>
    </main>
  );
};
